import { useState, useEffect, useCallback } from 'react';
import MapIcon from '@mui/icons-material/Map';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import axios from '../../auth/axiosConfig';

const GoogleStreetViewSimple = ({ initialAddress }) => {
  const removeAddressLine2 = (address) => {
    return address
      .replace(/,?\s*(unit|apt|apartment|suite|ste|floor|fl|room|rm|building|bldg|box|po box|p\.o\. box|lot|space|#)\s*[#\w-]+/gi, '')
      .replace(/,?\s*(north|south|east|west|n|s|e|w)\s+(unit|apt|apartment|suite|ste|floor|fl|room|rm|building|bldg)\s*[#\w-]+/gi, '')
      .replace(/,?\s*(upper|lower|front|rear|back)\s+(unit|apt|apartment|suite|ste|floor|fl)\s*[#\w-]+/gi, '')
      .replace(/,?\s*-\s*[#\w-]+/gi, '') // handles "200 Main St - A"
      .replace(/,\s*$/, '')
      .replace(/\s+/g, ' ')
      .trim();
   };
  const [address] = useState(removeAddressLine2(initialAddress));
  const [apiKey] = useState(process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mapData, setMapData] = useState(null);
  const [viewMode, setViewMode] = useState('street'); // street || map

  const fetchMapData = useCallback(async () => {
    if (!address.trim()) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.get(`/ui/google-maps/geocode-and-streetview?address=${encodeURIComponent(address)}`);
      const data = response.data;

      if (data.success) {
        setMapData(data);
        setViewMode(data.streetview_available ? 'street' : 'map');
        setError('');
      } else {
        setError('Failed to load map data');
        setMapData(null);
      }
    } catch (err) {
      console.error('Map data fetch error:', err);
      
      let errorMessage = 'Failed to load map data. Please try again.';
      
      if (err.response) {
        // Server responded with error status
        const detail = err.response.data?.detail || '';
        if (detail.includes('No results found')) {
          errorMessage = 'No results found for this address. Please try a different address.';
        } else if (detail.includes('query limit') || err.response.status === 429) {
          errorMessage = 'Service temporarily unavailable. Please try again later.';
        } else if (err.response.status === 403) {
          errorMessage = 'Map service access denied. Please contact support.';
        } else if (err.response.status === 503) {
          errorMessage = 'Map service temporarily unavailable. Please try again later.';
        } else if (err.response.status === 404) {
          errorMessage = 'Map service endpoint not found. Please contact support.';
        }
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setError(errorMessage);
      setMapData(null);
    } finally {
      setLoading(false);
    }
  }, [address]);

  // Get appropriate URL based on view mode
  const getCurrentViewUrl = () => {
    if (!mapData) return '';

    return viewMode === 'street' ? `${mapData.streetview_url}&key=${apiKey}` :
     `${mapData.map_url}&key=${apiKey}`;
  };

  useEffect(() => {
    // When address is available, fetch map data
    if (address) {
      fetchMapData();
    }
  }, [address, fetchMapData]);

  const toggleViewMode = () => {
    if (mapData && mapData.streetview_available) {
      setViewMode(viewMode => viewMode === 'street' ? 'map' : 'street');
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        bgcolor: 'grey.100',
        borderRadius: 1,
        overflow: 'hidden'
      }}
    >
      {mapData ? (
        <Box sx={{ height: '100%', position: 'relative' }}>
          <IconButton
            onClick={toggleViewMode}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 10,
              bgcolor: 'white',
              '&:hover': {
                bgcolor: 'grey.100',
              }
            }}
            title={viewMode === 'street' ? 'Switch to Map View' : 'Switch to Street View'}
            disabled={!mapData.streetview_available}
          >
            {viewMode === 'street' ? <MapIcon /> : <VisibilityIcon />}
          </IconButton>

          <iframe
            title="Google Maps"
            width="100%"
            height="100%"
            frameBorder="0"
            style={{ border: 0 }}
            src={getCurrentViewUrl()}
            allowFullScreen
          />

          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              left: 8,
              bgcolor: 'rgba(255,255,255,0.8)',
              px: 1,
              py: 0.5,
              borderRadius: 1
            }}
          >
            <Typography variant="caption">
              {mapData.coordinates.lat.toFixed(6)}, {mapData.coordinates.lng.toFixed(6)}
            </Typography>
          </Box>
        </Box>
      ) : loading ? (
        <Box sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="body2" color="textSecondary">
            Loading map data...
          </Typography>
        </Box>
      ) : error ? (
        <Box sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          textAlign: 'center'
        }}>
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        </Box>
      ) : (
        <Box sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <Typography variant="body2" color="textSecondary">
            No map data available for this address
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default GoogleStreetViewSimple;